import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, Trash2, Calendar, Infinity, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useSeason } from '@/contexts/SeasonContext';
import { TeamLogo } from '@/lib/teamLogos';
import { RightIcon } from '@/components/IconPicker';
import api from '@/lib/api';
import { QuantityField } from '@/components/QuantityField';

export default function CreateRequestPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, hasPerm } = useAuth();
  const { seasonOpen } = useSeason();
  const [matches, setMatches] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [limits, setLimits] = useState({});
  const [limitsLoading, setLimitsLoading] = useState({});
  const [sponsors, setSponsors] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState('');
  const [selectedSponsor, setSelectedSponsor] = useState('');
  const [items, setItems] = useState([]);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const isManager = hasPerm('create_requests_on_behalf');

  useEffect(() => {
    const promises = [
      api.get('/api/matches', { params: { status: 'upcoming,today,tomorrow' } }),
    ];
    if (isManager) {
      promises.push(api.get('/api/sponsors'));
    }
    Promise.all(promises).then(results => {
      let matchList = results[0].data;
      if (isManager && results[1]) {
        setSponsors(results[1].data);
      }
      // Pre-select match from URL params (may include ended matches from Matches page)
      const matchIdParam = searchParams.get('match_id');
      if (matchIdParam) {
        setSelectedMatch(matchIdParam);
        // If the pre-selected match isn't in the list (e.g. ended), fetch it individually
        if (!matchList.find(m => m.id === matchIdParam)) {
          api.get('/api/matches').then(allRes => {
            const found = allRes.data.find(m => m.id === matchIdParam);
            if (found) {
              matchList = [found, ...matchList];
              setMatches(matchList);
            }
          });
        }
      }
      setMatches(matchList);
    });
  }, []);

  useEffect(() => {
    // Inactive/archived rights can never be requested — drop them for every role, admin included
    const onlyActive = (list) => list.filter(a => (a.right_status || 'active') === 'active');
    if (isManager && selectedSponsor) {
      api.get(`/api/assignments?sponsor_id=${selectedSponsor}`).then(r => setAssignments(onlyActive(r.data)));
    } else if (!isManager) {
      api.get('/api/assignments').then(r => setAssignments(onlyActive(r.data)));
    }
  }, [selectedSponsor]);

  const addItem = () => setItems(prev => [...prev, { assignment_id: '', right_id: '', quantity: 1 }]);
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      if (field === 'assignment_id') {
        const a = assignments.find(a => a.id === value);
        return { ...it, assignment_id: value, right_id: a?.right_id || '' };
      }
      return { ...it, [field]: value };
    }));
  };

  const usedAssignmentIds = items.map(i => i.assignment_id).filter(Boolean);

  // Live ceiling per item: the lowest of remaining allocation, per-match limit and
  // per-round limit. Keyed by assignment + match, since the match changes what is
  // already booked.
  const limitKey = (assignmentId) => `${assignmentId}|${selectedMatch || ''}`;

  useEffect(() => {
    items.forEach(it => {
      if (!it.assignment_id) return;
      const key = limitKey(it.assignment_id);
      if (limits[key] !== undefined || limitsLoading[key]) return;
      setLimitsLoading(p => ({ ...p, [key]: true }));
      api.get(`/api/requests/limits?sponsor_right_assignment_id=${it.assignment_id}${selectedMatch ? `&match_id=${selectedMatch}` : ''}`)
        .then(r => setLimits(p => ({ ...p, [key]: r.data })))
        .catch(() => setLimits(p => ({ ...p, [key]: null })))
        .finally(() => setLimitsLoading(p => ({ ...p, [key]: false })));
    });
  }, [items, selectedMatch]);

  const maxFor = (assignmentId) => {
    const c = limits[limitKey(assignmentId)];
    return c && c.effective_max !== null && c.effective_max !== undefined ? c.effective_max : undefined;
  };

  // A right capped at 1 has nothing to decide, so pin it as soon as the cap is known
  useEffect(() => {
    setItems(prev => prev.map(it => {
      if (!it.assignment_id) return it;
      const max = maxFor(it.assignment_id);
      if (max === 1 && String(it.quantity) !== '1') return { ...it, quantity: '1' };
      return it;
    }));
  }, [limits]);

  const blockedItem = items.find(it => {
    const max = it.assignment_id ? maxFor(it.assignment_id) : undefined;
    return max !== undefined && (max === 0 || parseInt(it.quantity, 10) > max);
  });

  const handleSubmit = async () => {
    // Check if all items require a match
    const allItemsRequireMatch = items.every(i => {
      const a = assignments.find(a => a.id === i.assignment_id);
      return a?.match_required !== false;
    });
    if (allItemsRequireMatch && !selectedMatch) { toast.error(t('select_a_match')); return; }
    if (items.length === 0) { toast.error(t('add_one_item')); return; }
    if (isManager && !selectedSponsor) { toast.error(t('select_sponsor')); return; }
    for (const item of items) {
      if (!item.assignment_id || !item.quantity || item.quantity <= 0) {
        toast.error(t('fill_all_items')); return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        match_id: selectedMatch || null,
        items: items.map(i => ({ sponsor_right_assignment_id: i.assignment_id, right_id: i.right_id, requested_quantity: parseInt(i.quantity) })),
        sponsor_note: note,
      };
      if (isManager) {
        payload.sponsor_id = selectedSponsor;
        await api.post('/api/requests/on-behalf', payload);
        toast.success(t('request_created_on_behalf'));
      } else {
        await api.post('/api/requests', payload);
        toast.success(t('request_submitted'));
      }
      navigate('/requests');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit request'); }
    finally { setSubmitting(false); }
  };

  // Block sponsors from creating requests when season is closed
  if (!seasonOpen && !hasPerm('create_requests_on_behalf')) {
    return (
      <div data-testid="season-closed-create-request" className="flex flex-col items-center justify-center h-64 text-center">
        <Lock size={32} className="text-amber-500 mb-3" />
        <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Season Closed</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm">New requests cannot be created while the season is closed.</p>
        <Button variant="outline" onClick={() => navigate('/dashboard')} className="mt-4 text-sm">Back to Dashboard</Button>
      </div>
    );
  }

  return (
    <div data-testid="create-request-page" className="space-y-5 animate-fade-in max-w-[920px]">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/requests')} className="text-base"><ArrowLeft size={16} className="mr-1" /> {t('back')}</Button>
        <h1 className="text-xl font-semibold text-gray-900">{t('new_request')}</h1>
      </div>

      <Card className="bg-white border-gray-200/80 shadow-none">
        <CardHeader><CardTitle className="section-title flex items-center gap-2.5"><span className="h-6 w-6 rounded-full bg-[var(--ink)] text-[var(--surface)] text-[12px] tabular-nums flex items-center justify-center shrink-0">1</span>{t('select_match_step')}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {isManager && (
            <div>
              <Label className="text-sm text-gray-600 mb-1">{t('select_sponsor')} *</Label>
              <Select value={selectedSponsor} onValueChange={v => { setSelectedSponsor(v); setItems([]); }}>
                <SelectTrigger data-testid="select-sponsor" className="bg-gray-50 border-gray-300 h-11 text-base"><SelectValue placeholder={t('choose_sponsor')} /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {sponsors.filter(s => s.status === 'active').map(s => <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {assignments.some(a => a.match_required === false) && (
            <p className="text-xs text-gray-400">Some rights don&apos;t require a match. You can skip match selection for those.</p>
          )}
          <Select value={selectedMatch} onValueChange={setSelectedMatch}>
            <SelectTrigger data-testid="select-match" className="bg-gray-50 border-gray-300 h-11 text-base"><SelectValue placeholder={t('choose_match')} /></SelectTrigger>
            <SelectContent className="bg-white border-gray-200 max-h-72">
              {(() => {
                const grouped = {};
                matches.forEach(m => {
                  const round = m.round || 'Other';
                  if (!grouped[round]) grouped[round] = [];
                  grouped[round].push(m);
                });
                const sortedRounds = Object.keys(grouped).sort((a, b) => {
                  const numA = parseInt(a.replace(/\D/g, '')) || 999;
                  const numB = parseInt(b.replace(/\D/g, '')) || 999;
                  return numA - numB;
                });
                return sortedRounds.map(round => (
                  <div key={round}>
                    <div className="px-2 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider sticky top-0 bg-white">{round}</div>
                    {grouped[round].map(m => (
                      <SelectItem key={m.id} value={m.id}>
                        <div className="flex items-center gap-2">
                          <TeamLogo name={m.home_team} badgeUrl={m.home_team_badge} size={18} />
                          <span className="text-sm">{m.home_team}</span>
                          <span className="text-gray-400 text-xs">vs</span>
                          <TeamLogo name={m.away_team} badgeUrl={m.away_team_badge} size={18} />
                          <span className="text-sm">{m.away_team}</span>
                          <span className="text-gray-400 text-xs ml-1">({m.match_date})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </div>
                ));
              })()}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="bg-white border-gray-200/80 shadow-none">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="section-title flex items-center gap-2.5"><span className="h-6 w-6 rounded-full bg-[var(--ink)] text-[var(--surface)] text-[12px] tabular-nums flex items-center justify-center shrink-0">2</span>{t('select_rights_step')}</CardTitle>
          <Button data-testid="add-item-btn" variant="outline" size="sm" onClick={addItem} className="border-gray-300 text-base"><Plus size={14} className="mr-1" /> {t('add_item')}</Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {items.length === 0 && <p className="text-gray-400 text-base">{t('click_add')}</p>}
          {items.map((item, idx) => {
            const a = assignments.find(a => a.id === item.assignment_id);
            const avail = assignments.filter(a => !usedAssignmentIds.includes(a.id) || a.id === item.assignment_id);
            return (
              <div key={idx} className="p-4 rounded-xl bg-gray-50 border border-gray-100 space-y-3" data-testid={`request-item-${idx}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                      <Label className="text-sm text-gray-600">{t('right')}</Label>
                      <Select value={item.assignment_id} onValueChange={v => updateItem(idx, 'assignment_id', v)}>
                        <SelectTrigger className="bg-white border-gray-300 h-11 text-base"><SelectValue placeholder={t('select_right')} /></SelectTrigger>
                        <SelectContent className="bg-white border-gray-200">
                          {avail.map(a => (
                            <SelectItem key={a.id} value={a.id}>
                              <div className="flex items-center gap-2">
                                {a.right_icon_url && <RightIcon iconUrl={a.right_icon_url} size={16} className="text-gray-500" />}
                                <span>{a.right_name}</span>
                                <span className="text-gray-400 text-xs">
                                  {a.allocation_type === 'unlimited' ? `(${t('unlimited')} · ${a.used_amount || 0} ${t('used')})` : `(can request: ${a.available_to_request})`}
                                </span>
                                {a.match_required === false && <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 border-gray-300 text-gray-400">No match</Badge>}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600">{t('quantity')}</Label>
                      <QuantityField
                        testId={`request-item-${idx}-quantity`}
                        value={item.quantity}
                        onChange={v => updateItem(idx, 'quantity', v)}
                        max={item.assignment_id ? maxFor(item.assignment_id) : undefined}
                        loading={!!(item.assignment_id && limitsLoading[limitKey(item.assignment_id)])}
                      />
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="text-red-500 ml-2 mt-6"><Trash2 size={16} /></Button>
                </div>
                {a && a.allocation_type === 'limited' && (
                  <div className="bg-white border border-gray-100 rounded-lg p-3 space-y-1.5">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                      <div className="text-center p-2 bg-gray-50 rounded-md">
                        <div className="text-gray-400">Season Total</div>
                        <div className="font-bold text-gray-900 text-base">{a.allocated_amount}</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded-md">
                        <div className="text-gray-400">Used</div>
                        <div className="font-bold text-gray-700 text-base">{a.used_amount || 0}</div>
                      </div>
                      <div className="text-center p-2 bg-green-50 rounded-md border border-green-100">
                        <div className="text-green-600">Can Request</div>
                        <div className="font-bold text-green-700 text-base">{a.available_to_request}</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 rounded-md">
                        <div className="text-gray-400">Remaining</div>
                        <div className="font-bold text-gray-700 text-base">{a.remaining_amount}</div>
                      </div>
                    </div>
                    {(a.per_match_limit || a.per_round_limit) && (
                      <div className="flex gap-2 mt-1">
                        {a.per_match_limit && (
                          <span className="text-[11px] px-2 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">Max {a.per_match_limit}/match</span>
                        )}
                        {a.per_round_limit && (
                          <span className="text-[11px] px-2 py-0.5 rounded bg-purple-50 text-purple-600 font-medium">Max {a.per_round_limit}/round</span>
                        )}
                      </div>
                    )}
                    {a.pending_amount > 0 && (
                      <p className="text-[11px] text-amber-600 mt-1">
                        Note: {a.pending_amount} are held in pending requests not yet reviewed
                      </p>
                    )}
                  </div>
                )}
                {a && a.allocation_type === 'unlimited' && (
                  <div className="text-xs text-emerald-600 font-medium flex items-center gap-1 flex-wrap bg-emerald-50 border border-emerald-100 rounded-lg p-2.5">
                    <Infinity size={14} /> Unlimited — no quantity cap
                    {a.per_match_limit && <span className="ml-2 text-blue-500">(max {a.per_match_limit}/match)</span>}
                    {a.per_round_limit && <span className="ml-1 text-purple-500">(max {a.per_round_limit}/round)</span>}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card className="bg-white border-gray-200/80 shadow-none">
        <CardHeader><CardTitle className="section-title flex items-center gap-2.5"><span className="h-6 w-6 rounded-full bg-[var(--ink)] text-[var(--surface)] text-[12px] tabular-nums flex items-center justify-center shrink-0">3</span>{t('notes_submit')}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-base text-gray-700">{t('notes')}</Label>
            <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder={t('any_notes')} className="bg-gray-50 border-gray-300 text-base" />
          </div>
          <Button data-testid="submit-request-btn" onClick={handleSubmit} disabled={submitting || !!blockedItem}
            className="w-full bg-gray-900 hover:bg-gray-800 text-sm h-11">
            {submitting ? t('submitting') : t('submit_request')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
